/**
 * Wave chat-identity-fix · 2026-05-26
 *
 * Helper defensivo pra resolver "quem é o usuário corrente" em rendering
 * de chat (e qualquer outra coisa que dependa de "é meu?"). O bug
 * original (reportado pelo JP em 2026-05-26):
 *
 *   1. Usuário mandou msg no celular.
 *   2. Bubble apareceu do lado do PEER, como se ele tivesse recebido.
 *   3. Mesma conv aberta no desktop renderizou certo.
 *
 * O DB tinha o author correto (desktop = source of truth visual). A
 * divergência só existia no mobile, o que aponta pro front comparando
 * `m.authorUserId === me.id` contra um `me.id` STALE no
 * localStorage do celular.
 *
 * Causa raiz típica: Zustand `persist` segura `user` no localStorage. Se
 * o user trocou de conta (ou um build antigo rodou + Service Worker
 * cacheou) o `user.id` persistido pode divergir do `JWT.sub` do token
 * ativo. O token é a única autoridade real — quem o backend reconhece
 * como autor é `JWT.sub`, não `localStorage.user.id`.
 *
 * Esse módulo expõe:
 *   - `decodeJwtPayloadUnsafe(token)` — base64-decode do payload do JWT.
 *     NÃO verifica assinatura (impossível no client; é OK porque o
 *     servidor já valida). Use só pra ler claims públicos como `sub`.
 *   - `getAuthoritativeUserId(state)` — função pura que devolve o id
 *     correto pra usar como "meId" no rendering. Prioriza `JWT.sub`.
 *   - `useAuthoritativeUserId()` — hook React reativo.
 *   - `auditAuthIdentityOnce(state)` — checa mismatch entre `me.id` e
 *     `JWT.sub`. Se detectar, limpa o auth store pra forçar re-login —
 *     a sessão tá inconsistente e qualquer coisa que dependa de "é meu"
 *     vai renderizar errado.
 */

import { useEffect, useMemo } from 'react';
import { useAuth } from '@/store/auth.store';
import { decodeJwtPayloadUnsafe } from '@/lib/jwt-decode';

// Re-export pra manter API estável pra consumidores existentes.
export { decodeJwtPayloadUnsafe };
export type { JwtPayloadShape } from '@/lib/jwt-decode';

/** Snapshot mínimo do auth state pra função pura. */
export interface AuthIdentitySnapshot {
  token: string | null;
  userId: string | null;
}

/**
 * Devolve o user id autoritativo pra rendering. Ordem de prioridade:
 *   1. `JWT.sub` do token ativo (autoridade — é o que o backend usa).
 *   2. `state.userId` (fallback se o JWT não puder ser decodificado).
 *
 * Devolve `null` se não houver nenhuma fonte confiável — caller deve
 * tratar (ex: não renderizar bubbles).
 */
export function getAuthoritativeUserId(state: AuthIdentitySnapshot): string | null {
  const fromJwt = decodeJwtPayloadUnsafe(state.token)?.sub ?? null;
  if (fromJwt) return fromJwt;
  return state.userId;
}

/**
 * Hook React reativo — devolve o id autoritativo do usuário corrente.
 * Re-renderiza quando token ou user mudam no store.
 *
 * Também roda um audit silencioso uma vez por montagem: se detectar
 * mismatch entre `me.id` persistido e `JWT.sub`, limpa o auth store
 * (próximo request bate em /login).
 */
export function useAuthoritativeUserId(): string | null {
  const token = useAuth((s) => s.token);
  const userId = useAuth((s) => s.user?.id ?? null);
  const meId = useMemo(
    () => getAuthoritativeUserId({ token, userId }),
    [token, userId],
  );
  useEffect(() => {
    auditAuthIdentityOnce({ token, userId });
  }, [token, userId]);
  return meId;
}

/**
 * Checa mismatch entre `me.id` persistido e `JWT.sub`. Se positivo,
 * limpa o auth store pra forçar re-login — qualquer comparação "é
 * meu?" no app vai renderizar errado se eles divergirem.
 *
 * Idempotente: chamar várias vezes é seguro. Não faz nada se o token
 * for inválido (pode ser fluxo de logout em andamento) ou se não
 * houver user persistido.
 */
export function auditAuthIdentityOnce(state: AuthIdentitySnapshot): void {
  if (!state.token || !state.userId) return;
  const fromJwt = decodeJwtPayloadUnsafe(state.token)?.sub ?? null;
  if (!fromJwt) return;
  if (fromJwt === state.userId) return;
  // Mismatch — sessão inconsistente. Limpa.
  // eslint-disable-next-line no-console
  console.warn(
    '[auth-identity] mismatch detected: persisted user.id != JWT.sub. ' +
      'Clearing auth store to force re-login. ' +
      `persisted=${state.userId} jwtSub=${fromJwt}`,
  );
  try {
    useAuth.getState().clear();
  } catch {
    // Defensivo — se o store ainda não montou (SSR), swallow.
  }
}
